import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedBrokerageAccountListItem, SalesRegulatedApiService } from './sales-regulated-api.service';

@Component({
  standalone: true,
  selector: 'ain-sales-brokerage-account-detail',
  imports: [AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule, InputTextModule, CheckboxModule, CustomerSelectorComponent],
  template: `
    <ain-page [title]="isCreate() ? t('sales.regulated.brokerage.newAccount') : t('sales.regulated.brokerage.accountDetailTitle')" [description]="t('sales.regulated.brokerage.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <header class="card"><a class="back-link" [routerLink]="['/sales/brokerage-accounts']">{{ t('catalog.action.backToList') }}</a></header>
        @if (isCreate()) {
          <section class="card grid">
            <label><span>{{ t('sales.regulated.brokerage.fields.accountNumber') }}</span><input pInputText [(ngModel)]="form.accountNumber" /></label>
            <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerId" /></label>
            <label class="full"><p-checkbox [(ngModel)]="form.suitabilityApproved" [binary]="true" /><span>{{ t('sales.regulated.brokerage.fields.suitability') }}</span></label>
            <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
          </section>
        } @else if (account()) {
          <section class="summary card">
            <article><span>{{ t('sales.regulated.brokerage.fields.accountNumber') }}</span><strong>{{ account()!.accountNumber }}</strong></article>
            <article><span>{{ t('sales.regulated.brokerage.fields.suitability') }}</span><strong>{{ account()!.suitabilityApproved ? t('common.yes') : t('common.no') }}</strong></article>
          </section>
          <button pButton type="button" [label]="t('sales.regulated.brokerage.newOrder')" (click)="newOrder()"></button>
        }
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesBrokerageAccountDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreate = signal(false);
  readonly account = signal<RegulatedBrokerageAccountListItem | null>(null);
  form = { accountNumber: '', customerId: '' as string, suitabilityApproved: true };
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('accountId') ?? '';
    this.isCreate.set(!id || id === 'new');
    if (this.isCreate()) { this.loading.set(false); return; }
    this.api.listBrokerageAccounts().subscribe({
      next: rows => {
        this.account.set(rows.find(r => r.id === id) ?? null);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }
  save(): void {
    if (!this.form.customerId) return;
    this.api.createBrokerageAccount(this.form).subscribe({
      next: a => void this.router.navigate(['/sales/brokerage-accounts', a.id])
    });
  }
  newOrder(): void {
    const a = this.account();
    if (!a) return;
    void this.router.navigate(['/sales/brokerage-orders/new'], { queryParams: { accountId: a.id } });
  }
  t(key: string): string { return this.localization.translate(key); }
}
